import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { dealsApi } from "@/api/endpoints";
import { Note } from "@/components/ui/Note";
import { useI18n } from "@/i18n/use-i18n";

/**
 * Landing spot for the "Vorgang" tab, which has no deal id of its own.
 *
 * Resumes the caller's most recent unfinished Vorgang instead of silently
 * creating another one, and only starts a new deal when there is nothing open.
 */
export function NewDealPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const open = useQuery({
    queryKey: ["deals", "open", ""],
    queryFn: () => dealsApi.list("open", ""),
  });

  const createDeal = useMutation({
    mutationFn: dealsApi.create,
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      void queryClient.invalidateQueries({ queryKey: ["deal-stats"] });
      navigate(`/vorgang/${created.id}`, { replace: true });
    },
  });

  const mostRecent = open.data?.[0];

  useEffect(() => {
    if (open.isSuccess && !mostRecent && !createDeal.isPending && !createDeal.isError) {
      createDeal.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open.isSuccess, mostRecent]);

  if (mostRecent) return <Navigate to={`/vorgang/${mostRecent.id}`} replace />;

  if (open.isError || createDeal.isError) {
    return (
      <div className="page-shell py-10">
        <Note tone="error">
          {((open.error ?? createDeal.error) as Error).message}
        </Note>
      </div>
    );
  }

  return (
    <p className="page-shell py-10 text-muted">{t("deal.preparing")}</p>
  );
}
